import { useEffect, useRef } from 'react';
import { io } from 'socket.io-client';

/** A map of realtime event name to its handler. */
export type RealtimeHandlers = Record<string, (payload: unknown) => void>;

/**
 * Subscribe to realtime events from the api's socket.io gateway at `url` (the api
 * origin, not /v1), with credentials so the session cookie authenticates the
 * handshake. Client-only; pass `enabled: false` while logged out.
 */
export function useRealtime(
	url: string,
	handlers: RealtimeHandlers,
	enabled = true,
	// Fires after a dropped connection is re-established, so callers can refetch what they missed.
	onReconnect?: () => void,
	// Fires on EVERY successful connection — the first one included, not just reconnects.
	// A caller that painted from a persisted cache uses this to revalidate the moment the
	// socket is live, so it isn't left routing off stale data until something happens to
	// change (PLAN-88).
	onConnect?: () => void,
): void {
	// Read the latest handlers through refs so new callbacks don't reconnect the socket.
	const handlersRef = useRef(handlers);
	handlersRef.current = handlers;
	const reconnectRef = useRef(onReconnect);
	reconnectRef.current = onReconnect;
	const connectRef = useRef(onConnect);
	connectRef.current = onConnect;

	// Re-run only when the set of event names changes, not on every render.
	const events = Object.keys(handlers).sort().join(',');

	useEffect(() => {
		if (!enabled) return;
		const socket = io(url, { withCredentials: true, forceNew: true });
		for (const name of events ? events.split(',') : []) {
			socket.on(name, (payload: unknown) => handlersRef.current[name]?.(payload));
		}
		const onConnected = () => connectRef.current?.();
		socket.on('connect', onConnected);
		const onReconnected = () => reconnectRef.current?.();
		socket.io.on('reconnect', onReconnected);
		return () => {
			socket.off('connect', onConnected);
			socket.io.off('reconnect', onReconnected);
			socket.disconnect();
		};
	}, [url, events, enabled]);
}
